<clp-toolbar [title]="('digitalProduct.title' | translate)">
    <a
      [routerLink]="['/createDigitalProduct']"
      routerLinkActive="activeClass"
      class="clp-btn clp-btn-warning clp-btn-sm inline-flex items-center gap-2">
      <i class="fa-solid fa-plus"></i>
      <span>{{'general.create' | translate}}</span>
    </a>
</clp-toolbar>

<div class="px-4 py-3">
  @if (digitalProducts$ | async; as digitalProducts) {
    <div class="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{'digitalProduct.title' | translate}}</p>
        <p class="mt-1 text-xl font-semibold text-slate-100">{{ digitalProducts.length }}</p>
      </div>
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{ 'digitalProduct.visibility.1' | translate }}</p>
        <p class="mt-1 text-xl font-semibold text-slate-200">{{ getCountByVisibility(digitalProducts, 1) }}</p>
      </div>
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{ 'digitalProduct.visibility.2' | translate }}</p>
        <p class="mt-1 text-xl font-semibold text-emerald-300">{{ getCountByVisibility(digitalProducts, 2) }}</p>
      </div>
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{ 'digitalProduct.visibility.3' | translate }}</p>
        <p class="mt-1 text-xl font-semibold text-amber-300">{{ getCountByVisibility(digitalProducts, 3) }}</p>
      </div>
    </div>

    <div class="mb-3 rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
      <div class="grid gap-2 md:grid-cols-[minmax(0,1fr)_200px_200px]">
        <div>
          <label class="mb-1 block text-xs uppercase tracking-wide text-slate-400">{{ 'digitalProduct.name' | translate }}</label>
          <div class="relative">
            <i class="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500"></i>
            <input
              type="text"
              class="clp-input pl-9"
              [value]="searchTerm"
              (input)="onSearchChange($event)"
              [attr.placeholder]="'digitalProduct.searchPlaceholder' | translate" />
          </div>
        </div>
        <div>
          <label class="mb-1 block text-xs uppercase tracking-wide text-slate-400">{{ 'digitalProduct.billingTypeLabel' | translate }}</label>
          <select class="clp-select" [value]="billingTypeFilter" (change)="onBillingTypeFilterChange($event)">
            <option value="all">{{ 'digitalProduct.allBillingTypes' | translate }}</option>
            <option value="1">{{ 'digitalProduct.billingType.1' | translate }}</option>
          </select>
        </div>
        <div>
          <label class="mb-1 block text-xs uppercase tracking-wide text-slate-400">{{ 'digitalProduct.visibilityLabel' | translate }}</label>
          <select class="clp-select" [value]="visibilityFilter" (change)="onVisibilityFilterChange($event)">
            <option value="all">{{ 'digitalProduct.allVisibilities' | translate }}</option>
            <option value="1">{{ 'digitalProduct.visibility.1' | translate }}</option>
            <option value="2">{{ 'digitalProduct.visibility.2' | translate }}</option>
            <option value="3">{{ 'digitalProduct.visibility.3' | translate }}</option>
          </select>
        </div>
      </div>
    </div>

    @let filteredDigitalProducts = getFilteredDigitalProducts(digitalProducts);

    @if (digitalProducts.length === 0) {
      <div class="clp-card">
        <div class="clp-card-body flex flex-col items-center gap-3 py-10 text-center">
          <i class="fa-solid fa-box-open text-3xl text-slate-500"></i>
          <p class="text-sm text-slate-300">{{ 'digitalProduct.noDigitalProducts' | translate }}</p>
          <a [routerLink]="['/createDigitalProduct']" class="clp-btn clp-btn-warning clp-btn-sm">{{'general.create' | translate}}</a>
        </div>
      </div>
    } @else if (filteredDigitalProducts.length === 0) {
      <div class="clp-card">
        <div class="clp-card-body flex flex-col items-center gap-2 py-8 text-center">
          <i class="fa-solid fa-filter-circle-xmark text-2xl text-slate-500"></i>
          <p class="text-sm text-slate-300">{{ 'digitalProduct.noMatchingDigitalProducts' | translate }}</p>
        </div>
      </div>
    } @else {
      <div class="space-y-2">
        @for (digitalProduct of filteredDigitalProducts; track digitalProduct.id ?? $index) {
          <article class="group rounded-lg border border-slate-700 bg-slate-900/55 px-3 py-2.5 shadow-sm transition hover:border-amber-400/40 hover:bg-slate-900/75">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div class="min-w-0 space-y-1">
                <h3 class="truncate text-sm font-semibold text-slate-100">{{ digitalProduct.name }}</h3>
                @if (digitalProduct.description) {
                  <p class="truncate text-xs text-slate-400">{{ digitalProduct.description }}</p>
                }
                <p class="text-xs text-slate-300">
                  @if (isRecurring(digitalProduct.billingType)) {
                    {{ digitalProduct.recurringFee ?? 0 | number:'1.2-2' }} {{ digitalProduct.currency ?? 'USD' }}
                    /
                    {{ digitalProduct.recurringIntervalCount ?? 1 }}
                    {{ getRecurringIntervalLabelKey(digitalProduct.recurringInterval) | translate | lowercase }}
                  } @else {
                    {{ digitalProduct.oneTimeFee ?? 0 | number:'1.2-2' }} {{ digitalProduct.currency ?? 'USD' }}
                  }
                </p>
              </div>

              <div class="flex shrink-0 items-center gap-1.5">
                <span
                  class="rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                  [ngClass]="getVisibilityBadgeClass(digitalProduct.visibility)">
                  {{ getVisibilityLabelKey(digitalProduct.visibility) | translate }}
                </span>

                <span
                  class="rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                  [ngClass]="getBillingTypeBadgeClass(digitalProduct.billingType)">
                  {{ getBillingTypeLabelKey(digitalProduct.billingType) | translate }}
                </span>

                <button
                  class="clp-btn clp-btn-warning clp-btn-sm"
                  (click)="editDigitalProduct(digitalProduct.id)"
                  [attr.aria-label]="'general.edit' | translate"
                  [title]="'general.edit' | translate">
                  <i class="fa-regular fa-pen-to-square text-xs"></i>
                </button>

                <button
                  class="clp-btn clp-btn-danger clp-btn-sm"
                  (click)="openDeleteConfirmation(digitalProduct)"
                  [attr.aria-label]="'general.delete' | translate"
                  [title]="'general.delete' | translate">
                  <i class="fa-solid fa-trash-can text-xs"></i>
                </button>
              </div>
            </div>
          </article>
        }
      </div>
    }
  }
</div>

@if (digitalProductToDelete) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div
      class="absolute inset-0 bg-slate-950/70"
      (click)="closeDeleteConfirmation()"
      aria-hidden="true">
    </div>

    <section class="relative w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
      <div class="mb-4 flex items-start gap-3">
        <span class="flex h-9 w-9 items-center justify-center rounded-full border border-red-500/40 bg-red-500/15 text-red-300">
          <i class="fa-solid fa-triangle-exclamation text-sm"></i>
        </span>
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-slate-100">
            {{ 'digitalProduct.delete.confirmTitle' | translate }}
          </h3>
          <p class="mt-1 text-sm text-slate-300">
            {{ 'digitalProduct.delete.confirmMessage' | translate:{ name: (digitalProductToDelete.name ?? '') } }}
          </p>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="clp-btn clp-btn-secondary"
          [disabled]="deleting"
          (click)="closeDeleteConfirmation()">
          {{ 'general.cancel' | translate }}
        </button>
        <button
          type="button"
          class="clp-btn clp-btn-danger"
          [disabled]="deleting"
          (click)="confirmDeleteDigitalProduct()">
          {{ 'general.delete' | translate }}
        </button>
      </div>
    </section>
  </div>
}
